import { useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';

export function VendorCombobox({ 
  value, 
  vendors, 
  onChange, 
  onCreateVendor,
  disabled 
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const handleSelect = (selectedValue) => {
    onChange(selectedValue);
    setOpen(false);
    setInputValue('');
  };

  const handleCreate = async () => {
    if (!inputValue.trim()) return;
    
    const newVendor = await onCreateVendor(inputValue.trim());
    if (newVendor) {
      onChange(newVendor.name);
      setOpen(false);
      setInputValue('');
    }
  };

  const filteredVendors = vendors.filter(v =>
    v.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const exactMatch = vendors.some(
    v => v.name.toLowerCase() === inputValue.toLowerCase()
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between h-7 px-2 text-sm font-normal border-transparent hover:border-gray-300 bg-transparent"
          data-testid="vendor-combobox"
        >
          <span className={cn(!value && "text-gray-400")}>
            {value || "Vendor..."}
          </span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-48 p-0" align="start">
        <Command>
          <CommandInput 
            placeholder="Search or create..." 
            value={inputValue}
            onValueChange={setInputValue}
            data-testid="vendor-search-input"
          />
          <CommandList>
            <CommandEmpty>
              {inputValue && !exactMatch && (
                <button
                  onClick={handleCreate}
                  className="flex items-center gap-2 w-full px-2 py-1.5 text-sm hover:bg-gray-100 cursor-pointer"
                  data-testid="create-vendor-btn"
                >
                  <Plus className="h-4 w-4" />
                  Create "{inputValue}"
                </button>
              )}
              {!inputValue && <span className="text-gray-500 text-sm px-2">Type to search or create</span>}
            </CommandEmpty>
            <CommandGroup>
              {filteredVendors.map((v) => (
                <CommandItem
                  key={v.id}
                  value={v.name}
                  onSelect={() => handleSelect(v.name)}
                  data-testid={`vendor-option-${v.name}`}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === v.name ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {v.name}
                </CommandItem>
              ))}
              {inputValue && !exactMatch && filteredVendors.length > 0 && (
                <CommandItem
                  onSelect={handleCreate}
                  className="border-t"
                  data-testid="create-vendor-option"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create "{inputValue}"
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
